/**
 * ModoForjaAudioManager - Persistent audio component
 * Stays mounted to keep music playing when overlay is closed/minimized
 * Supports both local tracks and radio streams
 * Uses admin-configured defaults for initial audio source and selection
 */

import { useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useModoForja } from '@/contexts/ModoForjaContext';
import { useAuth } from '@/contexts/AuthContext';
import { fetchEnabledModoForjaTracks, MODOFORJA_ENABLED_TRACKS_QUERY_KEY } from '@/services/modoforjaTracks';
import { fetchRadioStations } from '@/services/radioStations';
import { useModoForjaSettings } from '@/hooks/useModoForjaSettings';
import { supabase } from '@/integrations/supabase/client';

// Map of track IDs to their public URLs
const trackUrlCache = new Map<string, string>();

// Helper to get track URL
function getTrackUrl(track: { id: string; file_path: string }): string {
  if (trackUrlCache.has(track.id)) {
    return trackUrlCache.get(track.id)!;
  }
  const { data } = supabase.storage
    .from('flowforja-music')
    .getPublicUrl(track.file_path);
  trackUrlCache.set(track.id, data.publicUrl);
  return data.publicUrl;
}

export default function ModoForjaAudioManager() {
  const { user } = useAuth();
  const { 
    isRadioPlaying, 
    setRadioPlaying,
    volume,
    isMuted,
    currentTrack,
    tracks,
    setTracks,
    setCurrentTrack,
    setTracksLoading,
    audioSource,
    setAudioSource,
    currentStation,
    stations,
    setStations,
    setCurrentStation,
    setStationsLoading,
  } = useModoForja();
  
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Fetch settings once and reuse
  // Gated by auth: visitors on /auth don't need Modo Forja audio config
  const { data: settings } = useModoForjaSettings({ enabled: !!user });

  const settingsLoaded = !!settings;

  // Apply default audio source from settings
  useEffect(() => {
    if (settings?.default_audio_source) {
      setAudioSource(settings.default_audio_source);
    }
  }, [settings, setAudioSource]);

  const { data: tracksData, isLoading: isTracksLoading } = useQuery({
    queryKey: MODOFORJA_ENABLED_TRACKS_QUERY_KEY,
    queryFn: fetchEnabledModoForjaTracks,
    enabled: !!user && settingsLoaded,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    setTracksLoading(isTracksLoading);
  }, [isTracksLoading, setTracksLoading]);

  useEffect(() => {
    if (!tracksData) return;

    tracksData.forEach(track => getTrackUrl(track));
    setTracks(tracksData);

    if (tracksData.length === 0) {
      if (currentTrack) {
        setCurrentTrack(null);
      }
      return;
    }

    const hasCurrentTrack = currentTrack && tracksData.some(track => track.id === currentTrack.id);
    if (!hasCurrentTrack) {
      if (settings?.default_track_id) {
        const defaultTrack = tracksData.find(track => track.id === settings.default_track_id);
        setCurrentTrack(defaultTrack || tracksData[0]);
      } else {
        setCurrentTrack(tracksData[0]);
      }
    }
  }, [tracksData, setTracks, currentTrack, setCurrentTrack, settings]);

  // Fetch available radio stations using React Query for cache invalidation sync
  const { data: stationsData, isLoading: isStationsLoading } = useQuery({
    queryKey: ['radio-stations'],
    queryFn: fetchRadioStations,
    enabled: !!user && settingsLoaded,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Sync stations data to context and set default station (using cached settings)
  useEffect(() => {
    if (!stationsData) return;
    
    setStations(stationsData);
    setStationsLoading(false);
    
    if (stationsData.length > 0 && !currentStation) {
      if (settings?.default_station_id) {
        const defaultStation = stationsData.find(s => s.id === settings.default_station_id);
        setCurrentStation(defaultStation || stationsData[0]);
      } else {
        setCurrentStation(stationsData[0]);
      }
    }
  }, [stationsData, settings, setStations, setCurrentStation, setStationsLoading, currentStation]);

  // Update loading state from query
  useEffect(() => {
    if (isStationsLoading) {
      setStationsLoading(true);
    }
  }, [isStationsLoading, setStationsLoading]);

  // Handle audio events - only for radio streams now
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleError = () => {
      console.warn('Audio playback error, stream may be unavailable');
      setRadioPlaying(false);
    };

    audio.addEventListener('error', handleError);
    return () => audio.removeEventListener('error', handleError);
  }, [setRadioPlaying]);

  // Update audio source when track or station changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    let newSrc: string | null = null;

    if (audioSource === 'local' && currentTrack) {
      newSrc = getTrackUrl(currentTrack);
    } else if (audioSource === 'radio' && currentStation) {
      newSrc = currentStation.stream_url;
    }

    if (newSrc && audio.src !== newSrc) {
      audio.src = newSrc;
      audio.volume = isMuted ? 0 : volume;

      if (isRadioPlaying) {
        audio.play().catch(() => setRadioPlaying(false));
      }
    }
  }, [audioSource, currentTrack, currentStation, isRadioPlaying, volume, isMuted, setRadioPlaying]);

  // Sync play state with context
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Check if we have valid source
    const hasSource = (audioSource === 'local' && currentTrack) || 
                      (audioSource === 'radio' && currentStation);
    
    if (!hasSource) return;

    if (isRadioPlaying) {
      audio.play().catch(() => setRadioPlaying(false));
    } else {
      audio.pause();
    }
  }, [isRadioPlaying, audioSource, currentTrack, currentStation, setRadioPlaying]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Invisible audio element that persists
  return <audio ref={audioRef} preload="auto" />;
}
